import { Injectable, inject, NgZone } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, of, from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthStore } from '../../store/auth.store';
import { ToastService } from '../../shared/services/toast.service';
import { NetworkService } from '../../shared/services/network.service';
import { GestionAccionesStateService } from '../../features/gestion/gestion-acciones.state';
import { AutenticacionService } from '../services/autenticacion.service';

/**
 * Interceptor HTTP que:
 * 1. Agrega automáticamente el token Bearer a las peticiones
 * 2. Verifica si el token ha expirado antes de cada petición
 * 3. Maneja respuestas 401 (no autorizado): invalida sesión API y conserva snapshot de campo.
 *    Sin red y con snapshot no fuerza /login. 403 no cierra sesión (suele ser permisos, no token).
 * 4. Si la app está en `/gestion/acciones/...` y el backend responde 403, redirige a `/gestion`, limpia el productor en sesión y avisa con toast (acceso a otro productor / sin permiso)
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly authStore = inject(AuthStore);
  private readonly autenticacionService = inject(AutenticacionService);
  private readonly router = inject(Router);
  private readonly ngZone = inject(NgZone);
  private readonly toastService = inject(ToastService);
  private readonly network = inject(NetworkService);
  private readonly gestionAccionesState = inject(GestionAccionesStateService);
  /** Evita múltiples redirecciones/toasts si varias peticiones fallan a la vez con 403. */
  private redirigiendo403DesdeAccionesGestion = false;
  /** Evita refrescar token en paralelo si varias peticiones fallan a la vez con 401. */
  private refrescandoToken = false;

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Excluir las peticiones de login de la verificación de token
    const esRutaPublica = this.esRutaPublica(req.url);
    const esPeticionRefresh = req.url.includes('/autenticar/refresh');
    const esPeticionLogout = req.url.includes('/autenticar/logout');

    // Si no es ruta pública y la petición NO trae su propio token, verificar si el token del store ha expirado
    if (!esRutaPublica && !esPeticionRefresh && !req.headers.has('Authorization') && this.authStore.obtenerEstadoAutenticacion()) {
      if (this.authStore.tokenExpirado()) {
        console.warn('Token expirado detectado antes de la petición. Intentando refrescar...');
        return this.intentarRefreshYReintentar(req, next);
      }
    }

    const token = this.authStore.obtenerToken();

    let requestConToken = req;
    if (token && !esRutaPublica && !req.headers.has('Authorization')) {
      requestConToken = req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`
        }
      });
    }

    return next.handle(requestConToken).pipe(
      catchError((error: HttpErrorResponse) => {
        // Solo 401 = no autenticado / token inválido. 403 suele ser falta de permiso (RBAC), no debe cerrar la sesión.
        if (error.status === 401) {
          // Solo si estamos autenticados y NO es la propia petición de refresh/login/logout,
          // intentar renovar la sesión con el refresh token.
          if (
            this.authStore.obtenerEstadoAutenticacion() &&
            !esRutaPublica &&
            !esPeticionRefresh &&
            !esPeticionLogout &&
            this.authStore.obtenerRefreshToken()
          ) {
            return this.intentarRefreshYReintentar(req, next);
          }
          // Si no podemos refrescar, cerrar sesión.
          if (this.authStore.obtenerEstadoAutenticacion() && !esRutaPublica) {
            console.warn('Error 401 recibido y no se pudo refrescar. Cerrando sesión...');
            this.manejarSesionExpirada();
          }
        } else if (error.status === 403) {
          this.manejar403EnVistaAccionesGestion();
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * Intenta renovar la sesión con el refresh token. Si tiene éxito, reintenta la
   * petición original con el nuevo access token. Si falla, cierra la sesión.
   */
  private intentarRefreshYReintentar(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.refrescandoToken) {
      // Ya hay una renovación en curso en otra petición con 401.
      return from(this.autenticacionService.refrescarToken()).pipe(
        switchMap((ok) => {
          if (!ok) {
            this.manejarSesionExpirada();
            return throwError(() => new Error('No se pudo renovar la sesión'));
          }
          const nuevoToken = this.authStore.obtenerToken();
          const reintento = req.clone({
            setHeaders: { Authorization: `Bearer ${nuevoToken ?? ''}` }
          });
          return next.handle(reintento);
        })
      );
    }

    this.refrescandoToken = true;
    return from(this.autenticacionService.refrescarToken()).pipe(
      switchMap((ok) => {
        this.refrescandoToken = false;
        if (!ok) {
          this.manejarSesionExpirada();
          return throwError(() => new Error('No se pudo renovar la sesión'));
        }
        const nuevoToken = this.authStore.obtenerToken();
        const reintento = req.clone({
          setHeaders: { Authorization: `Bearer ${nuevoToken ?? ''}` }
        });
        return next.handle(reintento);
      }),
      catchError((error) => {
        this.refrescandoToken = false;
        return throwError(() => error);
      })
    );
  }

  /**
   * Verifica si la URL es una ruta pública que no requiere token
   */
  private esRutaPublica(url: string): boolean {
    const rutasPublicas = [
      '/autenticar/login',
      '/autenticar/login/callback',
      '/autenticar/registro',
      '/autenticar/recuperar'
    ];
    return rutasPublicas.some(ruta => url.includes(ruta));
  }

  /**
   * Pantalla «Acciones por productor»: el backend puede responder 403 si el usuario altera la URL
   * o no tiene ámbito sobre ese productor. Se sale a la lista de gestión y se informa al usuario.
   */
  private manejar403EnVistaAccionesGestion(): void {
    const path = (this.router.url ?? '').split('?')[0].split('#')[0];
    if (!path.startsWith('/gestion/acciones/')) {
      return;
    }
    if (this.redirigiendo403DesdeAccionesGestion) {
      return;
    }
    this.redirigiendo403DesdeAccionesGestion = true;
    this.ngZone.run(() => {
      this.gestionAccionesState.clear();
      void this.router.navigate(['/gestion']).finally(() => {
        setTimeout(() => {
          this.redirigiendo403DesdeAccionesGestion = false;
        }, 800);
      });
      void this.toastService.mostrarAdvertencia(
        'No tiene permiso para ver la información de este productor en gestión.'
      );
    });
  }

  /**
   * JWT vencido o 401: conserva snapshot de campo. Sin red, entra a Ruta del día.
   */
  private manejarSesionExpirada(): void {
    this.authStore.invalidarSesionApiPreservandoCampo();

    const url = this.router.url ?? '';
    const yaEnLogin = url === '/login' || url.startsWith('/login?') || url === '/login2' || url.startsWith('/login2?');
    const yaEnRutaDia = url === '/ruta-del-dia' || url.startsWith('/ruta-del-dia/');
    const online = this.network.isOnline();

    if (this.authStore.debeEntrarRutaDelDiaSinRed(online)) {
      if (!yaEnRutaDia) {
        this.router.navigate(['/ruta-del-dia'], { replaceUrl: true });
      }
      return;
    }

    if (!yaEnLogin) {
      this.router.navigate(['/login'], {
        queryParams: { sesionExpirada: 'true' }
      });
    }
  }
}
